import React, { useState } from 'react';

import DataTable from '../shared/data_table';
import StatusPill from '../shared/status_pill';
import TeacherSlugLine from '../shared/teacher_slug_line';
import { MODE_REOPENED_NOW } from './params';

/*
 * the Close versions list: one row per teacher-month, net only (before, after, difference).
 * Props-only. rows: adapters.row_view; recalc: {teacher_uuid: {busy, text, failed}}.
 */
// Every SORT_KEYS key in both directions, worded as the order Ops see (not asc/desc).
const SORT_OPTIONS = [
  ['abs_net_diff', 'desc', 'Biggest difference first'],
  ['abs_net_diff', 'asc', 'Smallest difference first'],
  ['net_diff', 'desc', 'Largest increase first'],
  ['net_diff', 'asc', 'Largest decrease first'],
  ['teacher_name', 'asc', 'Teacher A–Z'],
  ['teacher_name', 'desc', 'Teacher Z–A'],
  ['baseline_net', 'desc', 'Highest before first'],
  ['baseline_net', 'asc', 'Lowest before first'],
  ['current_net', 'desc', 'Highest after first'],
  ['current_net', 'asc', 'Lowest after first'],
  ['version_count', 'desc', 'Most versions first'],
  ['version_count', 'asc', 'Fewest versions first'],
  ['changed_at', 'desc', 'Latest change first'],
  ['changed_at', 'asc', 'Earliest change first'],
  ['status', 'asc', 'Status A–Z'],
  ['status', 'desc', 'Status Z–A'],
  ['reopen_reason', 'asc', 'Reopen reason A–Z'],
  ['reopen_reason', 'desc', 'Reopen reason Z–A']
];

const DIFF_CLASS = { pos: 'tp-pos', neg: 'tp-neg', warn: 'tp-cv-warn', muted: 'tp-hint' };

const Side = ({ side }) =>
  side ? (
    <span>
      {side.text}
      {side.source_note ? <span className="tp-hint tp-cv-note">{side.source_note}</span> : null}
    </span>
  ) : (
    <span className="tp-hint">—</span>
  );

// The card view has no headers, so phones sort with this select.
export const SortSelect = ({ sort, on_change }) => (
  <label className="tp-cv-sortbar">
    <span className="tp-hint">Sort by</span>
    <select
      className="tp-select"
      value={`${sort.key}:${sort.dir}`}
      onChange={(e) => {
        const [key, dir] = e.target.value.split(':');
        on_change(key, dir);
      }}
    >
      {SORT_OPTIONS.map(([k, d, label]) => (
        <option key={`${k}:${d}`} value={`${k}:${d}`}>
          {label}
        </option>
      ))}
    </select>
  </label>
);

// Truncated in the table; pressing it (keyboard or touch) shows the whole reason in place.
const ReasonCell = ({ reason }) => {
  const [open, setOpen] = useState(false);
  if (!reason) return <span className="tp-hint">—</span>;
  return (
    <button
      type="button"
      className={`tp-link tp-cv-reason${open ? ' tp-cv-reason-open' : ''}`}
      aria-expanded={open}
      aria-label={`Reopen reason: ${reason}`}
      title={reason}
      onClick={() => setOpen((o) => !o)}
    >
      {reason}
    </button>
  );
};

const RecalcCell = ({ row, state, on_recalc }) => {
  if (!row.pending_recompute && !(state && state.text)) return null;
  const busy = !!(state && state.busy);
  return (
    <div className="tp-cv-recalc">
      {row.pending_recompute ? <StatusPill tone="orange">{row.pending_text}</StatusPill> : null}
      {row.pending_recompute ? (
        <button
          type="button"
          className="tp-link"
          aria-disabled={busy ? 'true' : undefined}
          aria-label={`Recalculate ${row.teacher_name}`}
          onClick={() => (busy ? undefined : on_recalc(row))}
        >
          {busy ? 'Recalculating…' : 'Recalculate'}
        </button>
      ) : null}
      {state && state.text ? (
        <span className={state.failed ? 'tp-neg' : 'tp-hint'} role="status">
          {state.text}
        </span>
      ) : null}
    </div>
  );
};

const VersionsTable = ({ mode, rows, sort, on_sort, on_open, recalc, on_recalc }) => {
  const reopened = mode === MODE_REOPENED_NOW;
  const columns = [
    {
      key: 'teacher',
      label: 'Teacher',
      sort_key: 'teacher_name',
      hideInCard: true,
      render: (r) => (
        <button
          type="button"
          className="tp-link"
          aria-label={`Open versions for ${r.teacher_name}`}
          onClick={() => on_open(r)}
        >
          <div>{r.teacher_name}</div>
          <TeacherSlugLine slug={r.teacher_slug} />
        </button>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sort_key: 'status',
      hideInCard: true,
      render: (r) => <StatusPill tone={r.status.tone}>{r.status.label}</StatusPill>
    },
    {
      key: 'versions',
      label: 'Versions',
      sort_key: 'version_count',
      align: 'right',
      render: (r) => r.version_count
    },
    {
      key: 'before',
      label: 'Before',
      sort_key: 'baseline_net',
      render: (r) => <Side side={r.before} />
    },
    {
      key: 'after',
      label: 'After',
      sort_key: 'current_net',
      render: (r) => <Side side={r.after} />
    },
    {
      key: 'diff',
      label: 'Difference',
      sort_key: 'abs_net_diff',
      align: 'right',
      render: (r) => <span className={`tp-strong ${DIFF_CLASS[r.diff.tone]}`}>{r.diff.text}</span>
    },
    {
      key: 'changed_at',
      label: 'Changed at',
      sort_key: 'changed_at',
      render: (r) => (
        <span>
          {r.changed_at}
          {r.changed_by ? <span className="tp-hint tp-cv-note">by {r.changed_by}</span> : null}
        </span>
      )
    },
    {
      key: 'reopen_reason',
      label: 'Reopen reason',
      sort_key: 'reopen_reason',
      render: (r) => <ReasonCell reason={r.reopen_reason} />
    }
  ];
  if (reopened) {
    columns.push({
      key: 'recalc',
      label: 'Figures',
      render: (r) => <RecalcCell row={r} state={recalc[r.teacher_uuid]} on_recalc={on_recalc} />
    });
  }
  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.key}
      sort={sort}
      on_sort={on_sort}
      onRowClick={on_open}
      cardTitle={(r) => (
        <>
          <button type="button" className="tp-link" onClick={() => on_open(r)}>
            {r.teacher_name}
          </button>
          <StatusPill tone={r.status.tone}>{r.status.label}</StatusPill>
        </>
      )}
    />
  );
};

export default VersionsTable;
